import { FlaggedReason } from "~/redux/warehouse/bins.openApi";
import { BinMaintenanceAssignment } from "~/types/api";

import {
  BULK_CLEAN_BINS_CLEANING_FLAGS,
  BULK_CLEAN_BINS_HOLD_FLAGS,
  BULK_CLEAN_BINS_TARGET_FLAGS,
  DeleteBinFlagTrigger,
  RemoveBinFlagsArgs
} from "./binSettings.types";

/** API PascalCase autostore bin types for display (e.g. `HighBin` → `High Bin`). */
export function formatAutostoreBinTypeDisplay(
  value: string | undefined
): string {
  return value ? value.replace(/([a-z])([A-Z])/g, "$1 $2") : "";
}

export function translationKeyForCount<
  TSingular extends string,
  TPlural extends string
>(
  singularKey: TSingular,
  pluralKey: TPlural,
  count: number
): TSingular | TPlural {
  return count === 1 ? singularKey : pluralKey;
}

export function getCleanableFlaggedBins(
  flaggedBins: BinMaintenanceAssignment[] | undefined
): BinMaintenanceAssignment[] {
  return (
    flaggedBins?.filter(
      (bin) => bin.flags.length > 0 && !bin.isCleanedAndOutsideOfGrid
    ) ?? []
  );
}

export function countCleanableFlaggedBins(
  flaggedBins: BinMaintenanceAssignment[] | undefined
): number {
  return getCleanableFlaggedBins(flaggedBins).length;
}

export function formatBinsToCleanWithPercent(
  remainingBinsToClean: number | undefined,
  totalBins: number | undefined
): string {
  if (remainingBinsToClean == null) {
    return "";
  }

  const formattedRemaining = remainingBinsToClean.toLocaleString("en-US");

  if (totalBins == null || totalBins <= 0) {
    return formattedRemaining;
  }

  const percentRemaining = Math.round((remainingBinsToClean / totalBins) * 100);
  return `${formattedRemaining} (${percentRemaining}%)`;
}

export function filterBinsBySelectedFlags(
  bins: BinMaintenanceAssignment[],
  selectedFlags: ReadonlySet<string>
): BinMaintenanceAssignment[] {
  if (selectedFlags.size === 0) {
    return [];
  }

  return bins.filter((bin) =>
    bin.flags.some((flag) => selectedFlags.has(flag))
  );
}

export function getSelectedFlagsForBin(
  bin: BinMaintenanceAssignment,
  selectedFlags: ReadonlySet<string>
): string[] {
  return bin.flags.filter((flag) => selectedFlags.has(flag));
}

export function countSelectedFlagsToRemove(
  bins: BinMaintenanceAssignment[],
  selectedFlags: ReadonlySet<string>
): number {
  return bins.reduce(
    (total, bin) => total + getSelectedFlagsForBin(bin, selectedFlags).length,
    0
  );
}

export function createDefaultSelectedFlagsArray(
  bins: BinMaintenanceAssignment[] = []
): FlaggedReason[] {
  const counts = getFlagCountsByType(bins);
  return BULK_CLEAN_BINS_CLEANING_FLAGS.filter((flag) => counts[flag] > 0);
}

export function countBinsWithFlag(
  bins: BinMaintenanceAssignment[],
  flag: FlaggedReason
): number {
  return bins.filter((bin) => bin.flags.includes(flag)).length;
}

export function getFlagCountsByType(
  bins: BinMaintenanceAssignment[]
): Record<FlaggedReason, number> {
  return Object.fromEntries(
    BULK_CLEAN_BINS_TARGET_FLAGS.map((flag) => [
      flag,
      countBinsWithFlag(bins, flag)
    ])
  ) as Record<FlaggedReason, number>;
}

export function countBinsWithCleaningFlags(
  bins: BinMaintenanceAssignment[]
): number {
  return filterBinsBySelectedFlags(
    bins,
    new Set(BULK_CLEAN_BINS_CLEANING_FLAGS)
  ).length;
}

export function countBinsWithHoldFlags(
  bins: BinMaintenanceAssignment[]
): number {
  return filterBinsBySelectedFlags(bins, new Set(BULK_CLEAN_BINS_HOLD_FLAGS))
    .length;
}

export function hasProcessableFlaggedBins(
  bins: BinMaintenanceAssignment[]
): boolean {
  return (
    countBinsWithCleaningFlags(bins) > 0 || countBinsWithHoldFlags(bins) > 0
  );
}

export type BulkCleanBinsDevHintKey =
  | "bulk clean bins dev hint"
  | "bulk clean bins blocked none flagged"
  | "bulk clean bins dev hint clear holds";

export function getBulkCleanBinsDevHintKey(
  bins: BinMaintenanceAssignment[]
): BulkCleanBinsDevHintKey {
  if (!hasProcessableFlaggedBins(bins)) {
    return "bulk clean bins blocked none flagged";
  }
  if (countBinsWithCleaningFlags(bins) === 0) {
    return "bulk clean bins dev hint clear holds";
  }
  return "bulk clean bins dev hint";
}

export function selectedFlagsIncludeCleaningFlag(
  selectedFlags: ReadonlySet<string>
): boolean {
  return BULK_CLEAN_BINS_CLEANING_FLAGS.some((flag) => selectedFlags.has(flag));
}

export async function removeBinFlags(
  deleteBinFlag: DeleteBinFlagTrigger,
  args: RemoveBinFlagsArgs
): Promise<void> {
  const {
    autostoreGridId,
    binNumber,
    flags,
    workstationId,
    deferCacheInvalidation
  } = args;

  for (const reason of flags) {
    await deleteBinFlag({
      autostoreGridId,
      binNumber,
      reason,
      workstationId,
      deferCacheInvalidation
    }).unwrap();
  }
}
